<script setup lang="ts">
import { Dialog } from "../../../../base-components/Headless";
import { FormLabel, FormInput } from "../../../../base-components/Form";
import Button from "../../../../base-components/Button";
import { reactive, ref } from "vue";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import { usePedidosStore } from "../../../../services/pedidos";
import FormSelect from "../../../../base-components/Form/FormSelect.vue";
import { useMensajesStore } from "../../../../services/mensajes";
import { LogAcciones, Pedido, PedidoItem } from "@fixeduy/restaurantestypes";
import { estadosPedidoItem } from "@fixeduy/restaurantestypes";
import { getCurrentTime } from "../../../../utils/fecha";
import { userUserStore } from "../../../../stores/user";
import { useLogAccionesStore } from "../../../../services/logAcciones";

interface Props {
  pedido: Pedido;
  item: PedidoItem;
}

const userStore = userUserStore();
const logAccionesStore = useLogAccionesStore();

const props = defineProps<Props>();
const sendButtonRef = ref(null);
const cargando = ref(false);

const pedidosStore = usePedidosStore();
const mensajesStore = useMensajesStore();

const emit = defineEmits<{
  (e: "cerrar"): void;
}>();

const formData = reactive({
  estado: props.item.estado || "pendiente"
});

const handleEdit = async () => {
  if (props.item.estado != formData.estado) {
    const logAcciones: LogAcciones = {
      id: "",
      accion: [
        `Se modifica estado del ítem ${props.item.nombre} del pedido N°${props.pedido.numero}: ${props.item.estado} -> ${formData.estado}`
      ],
      modulo: "comandas",
      fecha: getCurrentTime(),
      usuario: userStore.userLoggedIn?.uid!,
      mail: userStore.userLoggedIn?.email!
    };

    await logAccionesStore.guardarLog(logAcciones);
  }
  cargando.value = true;
  const result = await pedidosStore.modificarEstadoItem(
    props.pedido.id,
    props.item.id,
    formData.estado
  );
  cargando.value = false;
  if (result === true) {
    mensajesStore.mostrarToast(
      "Estado del ítem modificado",
      props.item.nombre,
      mensajesStore.mensajeType.exito
    );
    emit("cerrar");
  } else {
    mensajesStore.mostrarModal(
      "Error al actualizar ítem",
      result,
      mensajesStore.mensajeType.error
    );
  }
};
</script>

<template>
  <Dialog
    :open="true"
    @close="emit('cerrar')"
    :initialFocus="sendButtonRef"
    size="lg"
  >
    <Dialog.Panel class="overflow-hidden flex flex-col max-h-[85vh]">
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">Modificar estado del ítem</h2>
      </Dialog.Title>

      <Dialog.Description>
        <div class="flex">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="item-nombre" class="w-40 sw:w-20">
              Ítem
            </FormLabel>
          </div>
          <div class="flex-1 ml-2">
            <FormInput
              id="item-nombre"
              type="text"
              disabled
              :value="`${item.cantidad ?? 1} × ${item.nombre}`"
            />
          </div>
        </div>

        <div class="flex mt-5">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="item-estado" class="w-40 sw:w-20">
              Estado
            </FormLabel>
          </div>
          <div class="flex-1 ml-2">
            <FormSelect v-model.trim="formData.estado">
              <option
                v-for="opt in estadosPedidoItem"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </FormSelect>
          </div>
        </div>
      </Dialog.Description>

      <Dialog.Footer>
        <div v-if="!cargando" class="flex text-right">
          <div class="flex-1 text-left">
            <Button
              variant="secondary"
              class="mb-2 mr-1"
              @click="emit('cerrar')"
            >
              Cancelar
            </Button>
          </div>
          <div class="flex-1">
            <Button variant="primary" class="mb-2 mr-1" @click="handleEdit">
              Guardar
            </Button>
          </div>
        </div>
        <div v-else class="flex justify-center items-center">
          <LoadingIcon icon="oval" class="w-8 h-8" />
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
